import { ChangeEvent, useEffect, useState } from 'react';
import axios from 'axios';
import BaranDataGrid from '../common/BaranDataGrid.tsx';
import IResponse, { IPagination } from '../../interfaces/response.ts';
import { MAX_PAGE_LIMIT } from '../../constants/numbers.ts';
import { GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import numberFormat from '../../utils/numberFormat.ts';
import { useCheckoutStatusColors } from '../../enum/checkout.tsx';
import BasicModal from '../common/BasicModal.tsx';
import { Button, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

interface ICheckout {
	id: number;
	card_number: string;
	sheba_number: string;
	account_number: string;
	amount: number;
	status: string;
	reject_reason: string;
	user_id: number;
	created_at: string;
	updated_at: string;
}

export const Checkout = () => {
	const { t } = useTranslation();

	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [fetchedData, setFetchedData] = useState<ICheckout[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [showDialog, setShowDialog] = useState<null | { [key: string]: any }>(null);
	const [rejectDialog, setRejectDialog] = useState<number | null>(null);
	const [rejectReason, setRejectReason] = useState('');
	const [rejectReasonError, setRejectReasonError] = useState('');

	const headerClassNames = 'text-xs bg-background-paper';

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleViewCheckout = (checkoutId: number) => () => {
		axios({
			url: `/admin/settlements/${checkoutId}`,
			method: 'GET',
			loading: true,
		}).then(r => {
			if (r?.data?.success) {
				setShowDialog(r?.data?.data);
			}
		});
	};

	const closeDialog = () => setShowDialog(null);

	const closeRejectDislog = () => {
		setRejectDialog(null);
		setRejectReason('');
	};

	const openRejectDislog = (id: number) => () => setRejectDialog(id);

	const handleReject = () => {
		if (!rejectReason) {
			setRejectReasonError(t('enter_reject_reason'));
			return null;
		} else {
			setRejectReasonError('');
		}

		axios({
			url: `/admin/settlements/reject/${rejectDialog}`,
			method: 'POST',
			data: {
				reject_reason: rejectReason,
			},
		}).then(r => {
			if (r?.data?.success) {
				setFetchedData(prev => prev.map(i => ((i?.id ?? 0) === (rejectDialog ?? 0) ? { ...i, status: 'rejected' } : i)));
				closeRejectDislog();
			}
		});
	};

	const fetchCampaigns = () => {
		setLoading(true);
		axios<IResponse<ICheckout[]>>({
			url: '/admin/settlements',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const columns: GridColDef<ICheckout>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'amount',
			headerName: t('amount'),
			width: 120,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => numberFormat(params.row?.amount),
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 120,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const bgColorClass = useCheckoutStatusColors(params.value);
				return (
					<div className="flex h-full items-center justify-center">
						<div className={`flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{t(params.value)}</div>
					</div>
				);
			},
		},
		{
			field: 'user',
			headerName: t('user'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'card_number',
			headerName: t('card_number'),
			width: 160,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('created_at'),
			width: 160,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return <>{new Date(params.row?.created_at ?? '').toLocaleDateString('fa-IR')}</>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center',
			headerClassName: headerClassNames,
			align: 'center',
			renderCell: params => {
				return (
					<>
						{/*todo accpet*/}
						<Button className="mx-2 w-12 rounded-md bg-red-500 text-white" onClick={openRejectDislog(params.row.id)}>
							{t('rejected')}
						</Button>
						<IconButton onClick={handleViewCheckout(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		fetchCampaigns();
	}, [page, pageSize]);

	const changeReason = (e: any) => {
		setRejectReason(e?.target?.value);
	};

	return (
		<div className="w-full">
			<BaranDataGrid<ICheckout> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} />;
			<BasicModal open={!!showDialog} onClose={closeDialog} title={t('see_status')} titleClassName={'text-center grow '} subClassName={'flex flex-row-reverse'} className={'max-w-xl rounded-lg'} showBackIcon={false}>
				<div className="flex flex-col gap-3 py-4">
					<TextField label={t('card_number')} value={showDialog?.card_number ?? ''} disabled />
					<TextField label={t('sheba_number')} value={showDialog?.sheba_number ?? ''} disabled />
					<TextField label={t('account_number')} value={showDialog?.account_number ?? ''} disabled />
					<TextField label={t('amount')} value={numberFormat(showDialog?.amount) ?? ''} disabled />
					<TextField label={t('status')} value={t(showDialog?.status) ?? ''} disabled />
					<TextField label={t('created_at')} value={new Date(showDialog?.created_at ?? '').toLocaleDateString('fa-IR') ?? ''} disabled />
				</div>
			</BasicModal>
			<BasicModal
				open={!!rejectDialog}
				onClose={closeRejectDislog}
				title={t('reject')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-xl rounded-lg'}
				showBackIcon={false}
				action={
					<div className="flex w-full flex-row-reverse">
						<LoadingButton onClick={handleReject} variant="contained" color="primary" className="mx-2">
							{t('confirm')}
						</LoadingButton>
						<Button onClick={closeRejectDislog} variant="outlined" className="mx-2" color="error">
							{t('cancel')}
						</Button>
					</div>
				}
			>
				<TextField fullWidth className={'my-2'} multiline rows={4} value={rejectReason} onChange={changeReason} error={!!rejectReasonError} helperText={rejectReasonError} />
			</BasicModal>
		</div>
	);
};
